/**
 * membershipAgreements.service.ts — Admin > Membership Agreements: the
 * account's membership agreement templates, versioned, through
 * /v5/membership-agreements as the corporate dashboard edits them.
 *
 *   - Every save makes a new version: the API ends the current row (status 0)
 *     and adds one with the same groupID and the next version number.
 *   - The list is every row of every version; deleted and superseded rows
 *     both have status 0, so only the latest version of a group says whether
 *     the agreement itself is deleted.
 *   - Memberships point at an agreement by its groupID, so a new version is
 *     picked up without touching them.
 * Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AgreementVersion {
  membershipAgreementID: number;
  groupID: number;
  title: string;
  bodyHtml: string;
  version: number;
  active: boolean;
  createDate: string;
  endDate: string;
}

function normalize(row: Record<string, unknown>): AgreementVersion {
  const id = Number(row.membershipAgreementID ?? 0);
  return {
    membershipAgreementID: id,
    groupID: Number(row.groupID ?? 0) || id,
    title: String(row.title ?? ''),
    bodyHtml: String(row.bodyHtml ?? ''),
    version: Number(row.version ?? 1) || 1,
    active: row.active === true || Number(row.status) === 1,
    createDate: String(row.createDate ?? ''),
    endDate: String(row.endDate ?? ''),
  };
}

/** Every version of every agreement, deleted and superseded ones included. */
export function listAgreementVersions(): Promise<AgreementVersion[]> {
  // A bare ?include_inactive reaches the API as null and returns live rows only.
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/membership-agreements', { params: { include_inactive: 1 } })
    .then((r) => (Array.isArray(r.data?.data) ? r.data.data : []).map(normalize));
}

/** Today in the account's calendar, as the API's effectiveDate. */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function createAgreement(data: { title: string; bodyHtml: string }): Promise<void> {
  return apiClient.post('/membership-agreements', { ...data, effectiveDate: today() }).then(() => undefined);
}

/** Saves a new version of the agreement whose current row is `id`. */
export function saveAgreementVersion(id: number, data: { title: string; bodyHtml: string }): Promise<void> {
  return apiClient.put(`/membership-agreements/${id}`, { ...data, effectiveDate: today() }).then(() => undefined);
}

export function deleteAgreement(id: number): Promise<void> {
  return apiClient.delete(`/membership-agreements/${id}`).then(() => undefined);
}

export function restoreAgreement(id: number): Promise<void> {
  return apiClient.post(`/membership-agreements/${id}/restore`).then(() => undefined);
}

/** The latest version of each agreement (group), and all of each group's versions newest first. */
export function groupAgreements(rows: AgreementVersion[]): { latest: AgreementVersion[]; versions: Map<number, AgreementVersion[]> } {
  const versions = new Map<number, AgreementVersion[]>();
  for (const r of rows) {
    const list = versions.get(r.groupID) ?? [];
    list.push(r);
    versions.set(r.groupID, list);
  }
  versions.forEach((list) => list.sort((a, b) => b.version - a.version || b.membershipAgreementID - a.membershipAgreementID));
  return { latest: [...versions.values()].map((list) => list[0]), versions };
}
